import React, { useCallback, useState } from 'react';
import { ButtonV2, DialogV2, DialogV2Props, Flex, IconV2, TextV2, CheckboxV2, Accordion, LinkV2 } from '@withjoy/joykit';
import { Link, ChevronDown } from '@withjoy/joykit/icons';
import { ReactComponent as Warning } from '@apps/registry/admin/assets/Warning.svg';
import { DonationFundPlatformTypeEnum } from '@graphql/generated';
import { getPaymentMethodFormattedName } from '@shared/utils/paymentMethodOptions';
import { FormikProps } from 'formik';
import { useTranslation } from '@shared/core';
import { useAdminRegistryTelemetry } from '@apps/registry/admin/AdminRegistry.telemetry';
import { useMount } from '@shared/utils/hooks/useMount';
import { getPaymentUsernameFormat } from '@apps/registry/common/components/AddCashFund/helpers';
import { dialogStyles } from './AppPaymentDisclaimerDialog.styles';

interface AppPaymentDisclaimerDialogProps extends DialogV2Props {
  paymentMethod?: Maybe<DonationFundPlatformTypeEnum>;
  paymentLink?: string;
  paymentUsername?: string;
  formik: FormikProps<{ agreementChecked: boolean; trackingChecked: boolean }>;
  currencyCode?: string;
}

export const AppPaymentDisclaimerDialog: React.FC<AppPaymentDisclaimerDialogProps> = ({
  paymentMethod,
  paymentLink,
  paymentUsername,
  formik,
  currencyCode,
  onClose,
  ...dialogProps
}) => {
  const { t } = useTranslation('sharedRegistry');
  const translations = t('cashFundDisclaimerDialog');
  const { title, warning, paymentAccount, accordion, checkboxes, buttons } = translations;
  const telemetry = useAdminRegistryTelemetry();

  const [expandedSections, setExpandedSections] = useState<number[]>([]);

  useMount(() => {
    if (dialogProps.isOpen) {
      telemetry.cashfundDisclaimerDialog.enter();
    }
  });

  const handleAccordionChange = useCallback((indices: number[]) => {
    setExpandedSections(indices);
  }, []);

  const isSaveDisabled = !(formik.values.agreementChecked && formik.values.trackingChecked);

  const renderAccordionItem = (key: keyof typeof accordion, extraContent?: React.ReactNode) => (
    <Accordion.Item __css={dialogStyles.accordionItem}>
      {({ isExpanded }) => (
        <>
          <Accordion.Button __css={dialogStyles.accordionButton}>
            <Flex __css={dialogStyles.accordionButtonContent}>
              <TextV2
                typographyVariant="hed2"
                __css={dialogStyles.accordionButtonTitle}
              >
                {accordion[key].title()}
              </TextV2>
              <IconV2
                size="sm"
                __css={dialogStyles.accordionButtonIcon}
                style={{
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              >
                <ChevronDown />
              </IconV2>
            </Flex>
          </Accordion.Button>
          <Accordion.Panel __css={dialogStyles.accordionPanel}>
            <TextV2
              typographyVariant="body2"
              __css={dialogStyles.accordionPanelContent}
            >
              {accordion[key].content()}
            </TextV2>
            {extraContent}
          </Accordion.Panel>
        </>
      )}
    </Accordion.Item>
  );

  return (
    <DialogV2
      {...dialogProps}
      size="lg"
      stickyBehavior="header"
    >
      <DialogV2.Body>
        <Flex __css={dialogStyles.headerContainer}>
          <IconV2
            size="xxl"
            color="mono14"
          >
            <Warning />
          </IconV2>
          <TextV2
            typographyVariant="hed4"
            __css={dialogStyles.title}
          >
            {title().replace('{paymentMethod}', getPaymentMethodFormattedName(paymentMethod as string))}
          </TextV2>
          <TextV2
            typographyVariant="body1"
            __css={dialogStyles.warningText}
          >
            {warning.main().replace('{paymentMethod}', getPaymentMethodFormattedName(paymentMethod as string))}
          </TextV2>
        </Flex>

        <Flex __css={dialogStyles.paymentAccountContainer}>
          <TextV2
            typographyVariant="hed1"
            __css={dialogStyles.paymentAccountLabel}
          >
            {paymentAccount.label()}
          </TextV2>
          <Flex __css={dialogStyles.paymentUsernameContainer}>
            <TextV2
              __css={dialogStyles.paymentUsername}
              typographyVariant="hed1"
              as="a"
              href={paymentLink}
              target="_blank"
            >
              {getPaymentUsernameFormat(paymentMethod, currencyCode) || ''}
              {paymentUsername}
            </TextV2>
          </Flex>
          <ButtonV2
            variant="outline"
            intent="neutral"
            typographyVariant="button1"
            overrides={{ Root: { props: dialogStyles.testPaymentButton } }}
            onClick={() => window.open(paymentLink, '_blank')}
            shape="rounded"
          >
            {paymentAccount.send_test_payment()}
            <IconV2 __css={dialogStyles.testPaymentButtonIcon}>
              <Link size="sm" />
            </IconV2>
          </ButtonV2>
        </Flex>

        <TextV2
          typographyVariant="body1"
          __css={dialogStyles.testWarningText}
        >
          {paymentAccount.test_warning()}
        </TextV2>

        <Accordion
          enableMulti
          index={expandedSections}
          onChange={handleAccordionChange}
          __css={dialogStyles.accordionContainer}
        >
          {renderAccordionItem('howItWorks')}
          {renderAccordionItem('whatToKnow')}
          {renderAccordionItem(
            'wrongAccount',
            <LinkV2
              __css={dialogStyles.accordionPanelLink}
              href="https://withjoy.com/help/en/articles/8328259-how-do-i-find-my-paypal-me-venmo-or-cash-fund-app-username"
              target="_blank"
            >
              <TextV2
                typographyVariant="body1"
                __css={dialogStyles.accordionPanelLinkText}
              >
                {accordion.wrongAccount.learnMoreLink()}
              </TextV2>
            </LinkV2>,
          )}
        </Accordion>
        <form onSubmit={formik.handleSubmit}>
          <Flex __css={dialogStyles.checkboxesContainer}>
            <CheckboxV2
              name="agreementChecked"
              checked={formik.values.agreementChecked}
              onChange={formik.handleChange}
              typographyVariant="body2"
              color="mono12"
              overrides={dialogStyles.checkboxOverrides}
            >
              {checkboxes.agreement()}
            </CheckboxV2>
            <CheckboxV2
              name="trackingChecked"
              checked={formik.values.trackingChecked}
              onChange={formik.handleChange}
              typographyVariant="body2"
              color="mono12"
              overrides={dialogStyles.checkboxOverrides}
            >
              {checkboxes.tracking()}
            </CheckboxV2>
          </Flex>

          <Flex __css={dialogStyles.actionButtonsContainer}>
            <ButtonV2
              type="submit"
              shape="rounded"
              intent="neutral"
              width="100%"
              disabled={isSaveDisabled}
            >
              {buttons.accept()}
            </ButtonV2>
            <ButtonV2
              intent="neutral"
              variant="ghost"
              onClick={e => {
                e.preventDefault();
                onClose?.({ closeSource: 'custom' });
              }}
            >
              {buttons.cancel()}
            </ButtonV2>
          </Flex>
        </form>
      </DialogV2.Body>
    </DialogV2>
  );
};
